import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { ButtonComponent } from '../button/button.component';
import { EntityListSelectorComponent } from './entity-list-selector.component';
import { EntityListSelectorService } from './entity-list-selector.service';
import { EntityType } from './entity-list-selector.types';

/**
 * Global dialog component for entity selection.
 * Lives in app.component.html as a single instance.
 * Controlled via EntityListSelectorService signals.
 */
@Component({
  selector: 'app-entity-list-selector-dialog',
  imports: [EntityListSelectorComponent, ButtonComponent],
  templateUrl: './entity-list-selector-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './entity-list-selector-dialog.component.css',
})
export class EntityListSelectorDialogComponent {
  public service = inject(EntityListSelectorService);

  selectedItems = signal<any[]>([]);

  /**
   * Handle selection changes from the list component
   */
  onSelectionChange(items: any[]): void {
    this.selectedItems.set(items);
  }

  /**
   * Handle single item selection (single-select mode)
   */
  onSelect(item: any): void {
    this.selectedItems.set([item]);
  }

  /**
   * Confirm selection and close dialog
   */
  onConfirm(): void {
    this.service.confirm(this.selectedItems());
  }

  /**
   * Cancel and close dialog
   */
  onCancel(): void {
    this.service.cancel();
  }

  /**
   * Get default title based on entity type
   */
  getDefaultTitle(): string {
    const entityType = this.service.currentEntityType();
    if (!entityType) return 'Select Items';

    const titles: Record<EntityType, string> = {
      users: 'Select Users',
      locations: 'Select Locations',
    };

    return titles[entityType] || 'Select Items';
  }

  /**
   * Get confirm button text
   */
  getConfirmText(): string {
    const config = this.service.config();
    return config?.confirmText || 'Select';
  }

  /**
   * Get cancel button text
   */
  getCancelText(): string {
    const config = this.service.config();
    return config?.cancelText || 'Cancel';
  }

  /**
   * Check if confirm button should be disabled
   */
  isConfirmDisabled(): boolean {
    const config = this.service.config();
    const hasSelection = this.selectedItems().length > 0;

    // In single-select mode, require at least one selection
    if (config?.mode === 'single') {
      return !hasSelection;
    }

    // In multi-select mode, allow empty selection (user may want to clear)
    return false;
  }

  /**
   * Handle backdrop click (close dialog)
   */
  onBackdropClick(): void {
    this.onCancel();
  }

  /**
   * Handle keyboard shortcuts
   */
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.onCancel();
    } else if (event.key === 'Enter' && !this.isConfirmDisabled()) {
      this.onConfirm();
    }
  }
}
